import React from 'react'
import { defineMessages, FormattedMessage } from 'react-intl'

import {
  CostMessagingModalContent,
  CoverageType,
  EAP_MEDS_CONTENT_TYPE,
  EAP_NEURO_CONTENT_TYPE,
  SessionsCoverageText,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import {
  computeRemainderSessions,
  getSessionCountForDisplay,
  isUnlimitedSessionLimit,
  SESSIONS_COVERAGE_TITLE_ID,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/sessionLimit'
import { GetCostMessagingModalTextParams } from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import {
  SessionCountByProgram,
  SessionLimitByProgram,
} from '@lyrahealth-inc/shared-app-logic/src/models/session/SessionUsage'

const CUSTOMERS_WITH_SESSIONS_COPY = ['att', 'attunion']

interface EAP_COST_MESSAGING_PARAMS {
  numberOfSessions: string
  customerDisplayName: string
  isCustomerDynamicSessionLimitEnabled: boolean
  numberOfSessionsByProgram?: SessionLimitByProgram
  displayVisitsPerIssuePerYearByProgram?: SessionCountByProgram
  customerName?: string
  customerOffersCoaching?: boolean
  customerOffersTherapy?: boolean
  customerOffersNeuroskillsCoaching?: boolean
  customerOffersCaregiverTraining?: boolean
}

const sessionsCoverageMessages = defineMessages({
  [SESSIONS_COVERAGE_TITLE_ID.SESSIONS_TITLE]: {
    defaultMessage: '{numberOfSessions} sessions',
    description: 'Sessions coverage title with session count',
  },
  [SESSIONS_COVERAGE_TITLE_ID.TOTAL_SESSIONS_TITLE]: {
    defaultMessage: '{numberOfSessions} total sessions',
    description: 'Sessions coverage title for dual session entitlements',
  },
  [SESSIONS_COVERAGE_TITLE_ID.THERAPY_SESSIONS_TITLE]: {
    defaultMessage: '{numberOfSessions} therapy sessions',
    description: 'Sessions coverage title for therapy only with dependents',
  },
  [SESSIONS_COVERAGE_TITLE_ID.UNLIMITED_SESSIONS_TITLE]: {
    defaultMessage: 'Unlimited sessions',
    description: 'Sessions coverage title for unlimited sessions',
  },
  [SESSIONS_COVERAGE_TITLE_ID.COACHING_SESSIONS_TITLE]: {
    defaultMessage: '{numberOfSessions} coaching sessions',
    description: 'Sessions coverage title for coaching only with dependents',
  },

  // EAP Only + coaching (no therapy)
  eapOnlyHasCoachingOnlyWithNoDependentsDescription: {
    defaultMessage: 'Coaching sessions covered per year',
    description: 'Sessions coverage description for EAP without dependents',
  },
  eapOnlyHasCoachingOnlyWithDependentsDescription: {
    defaultMessage: 'Annual coaching for you and your dependents',
    description: 'Sessions coverage description for EAP only with dependents',
  },

  // EAP (therapy and coaching)
  eapNoDependentsDescription: {
    defaultMessage: 'Therapy and coaching covered per year',
    description: 'Sessions coverage description for EAP without dependents',
  },
  eapWithDependentsDescription: {
    defaultMessage: 'Annual therapy and coaching for you and your dependents',
    description: 'Sessions coverage description for EAP with dependents',
  },

  // EAP therapy only
  eapTherapyOnlyNoDependentsDescription: {
    defaultMessage: 'Therapy sessions covered per year',
    description: 'Sessions coverage description for EAP therapy only without dependents',
  },
  eapTherapyOnlyWithDependentsDescription: {
    defaultMessage: 'Annual therapy for you and your dependents',
    description: 'Sessions coverage description for EAP therapy only with dependents',
  },

  // EAP per issue per year
  eapPerIssueNoDependentsDescription: {
    defaultMessage: 'Therapy and coaching covered per issue, per year',
    description: 'Sessions coverage description for per issue per year without dependents',
  },
  eapPerIssueWithDependentsDescription: {
    defaultMessage: 'Therapy and coaching covered per issue, per year for you and your dependents',
    description: 'Sessions coverage description for per issue per year with dependents',
  },

  // EAP Coaching + HPI Therapy
  eapCoachingHpiTherapyWithDependentsDescription: {
    defaultMessage: 'Annual coaching for you and your dependents',
    description: 'Sessions coverage description for EAP coaching with HPI therapy',
  },
  eapCoachingHpiTherapyNoDependentsDescription: {
    // This case might not exist, adding just in case
    defaultMessage: 'Annual coaching covered per year',
    description: 'Sessions coverage description for EAP coaching with HPI therapy',
  },

  // HPI Only
  hpiNoDependentsDescription: {
    defaultMessage: 'Possible cost-sharing to meet with a provider',
    description: 'Sessions coverage description for HPI only without dependents',
  },
  hpiWithDependentsDescription: {
    defaultMessage: 'Possible cost-sharing for you and your dependents',
    description: 'Sessions coverage description for HPI only with dependents',
  },

  // Amazon
  coachingAndTherapySessionsTitle: {
    defaultMessage: '1-on-1 sessions',
    description: 'Sessions coverage title for mixed EAP and HPI coverage',
  },
  eapAndHpiMixedDescription: {
    defaultMessage: 'Therapy and coaching with possible cost-sharing',
    description: 'Sessions coverage description for mixed EAP and HPI coverage',
  },
})

export { sessionsCoverageMessages }

export const therapyOrCoachingTitle = (
  <FormattedMessage
    defaultMessage='Therapy or coaching sessions'
    description='Cost messaging modal for therapy or coaching sessions'
  />
)

export const therapySessionsTitle = (
  <FormattedMessage defaultMessage='Therapy sessions' description='Cost messaging modal title for therapy sessions' />
)

export const coachingSessionsTitle = (
  <FormattedMessage defaultMessage='Coaching sessions' description='Cost messaging modal title for coaching sessions' />
)

export const coachingForParentsTitle = (
  <FormattedMessage
    defaultMessage='Coaching for parents'
    description='Cost messaging modal title for coaching for parents sessions'
  />
)

export const therapyForChildrenTitle = (
  <FormattedMessage
    defaultMessage='Therapy for children'
    description='Cost messaging modal title for therapy for children sessions'
  />
)

const eapSessionForYourself = (
  numberOfSessions: string | number,
  customerDisplayName: string,
  isCustomerDynamicSessionLimitEnabled: boolean,
) => {
  if (isCustomerDynamicSessionLimitEnabled) {
    return eapSessionGeneric(customerDisplayName)
  }

  return (
    <FormattedMessage
      defaultMessage='Meet with a provider up to {numberOfSessions} times a year—covered by {customerDisplayName}.'
      description='Cost messaging modal session description text'
      values={{ numberOfSessions, customerDisplayName }}
    />
  )
}

const eapSessionForFamily = (
  numberOfSessions: string | number,
  customerDisplayName: string,
  isCustomerDynamicSessionLimitEnabled: boolean,
  customerName = '',
) => {
  if (isCustomerDynamicSessionLimitEnabled) {
    return eapSessionGeneric(customerDisplayName)
  }

  const hasSessionsCopy = CUSTOMERS_WITH_SESSIONS_COPY.includes(customerName)

  return (
    <FormattedMessage
      defaultMessage='Meet with a provider up to {numberOfSessions} {hasSessionsCopy, select, true {sessions} other {times}} a year—covered by {customerDisplayName} for you and your dependents.'
      description='Cost messaging modal session description for family text'
      values={{ numberOfSessions, customerDisplayName, hasSessionsCopy: hasSessionsCopy ? 'true' : 'false' }}
    />
  )
}

const getNeurodiversitySessionType = (offersNeuroskillsCoaching?: boolean, offersCaregiverTraining?: boolean) => {
  if (offersNeuroskillsCoaching && offersCaregiverTraining) return 'both'
  if (offersCaregiverTraining) return 'caregiverOnly'
  return 'neuroskillsOnly'
}

const neurodiversitySessionsForYourself = (
  numberOfSessions: string | number,
  offersNeuroskillsCoaching?: boolean,
  offersCaregiverTraining?: boolean,
) => {
  const sessionType = getNeurodiversitySessionType(offersNeuroskillsCoaching, offersCaregiverTraining)
  return (
    <FormattedMessage
      defaultMessage='Access {isUnlimited, select, true {unlimited} other {{numberOfSessions}}} {sessionType, select, caregiverOnly {caregiver training} both {neurodiversity skills coaching and caregiver training} other {neurodiversity skills coaching}} sessions per year.'
      description='Cost messaging modal session description text for neurodiversity sessions'
      values={{
        numberOfSessions,
        isUnlimited: isUnlimitedSessionLimit(Number(numberOfSessions)),
        sessionType,
      }}
    />
  )
}

const neurodiversitySessionsForFamily = (
  numberOfSessions: string | number,
  offersNeuroskillsCoaching?: boolean,
  offersCaregiverTraining?: boolean,
) => {
  const sessionType = getNeurodiversitySessionType(offersNeuroskillsCoaching, offersCaregiverTraining)
  return (
    <FormattedMessage
      defaultMessage='Access {isUnlimited, select, true {unlimited} other {{numberOfSessions}}} {sessionType, select, caregiverOnly {caregiver training} both {neurodiversity skills coaching and caregiver training} other {neurodiversity skills coaching}} sessions per year for you and your dependents 18+.'
      description='Cost messaging modal session description text for neurodiversity sessions'
      values={{
        numberOfSessions,
        isUnlimited: isUnlimitedSessionLimit(Number(numberOfSessions)),
        sessionType,
      }}
    />
  )
}

// Generic descriptions for dynamic session limits
export const eapSessionGeneric = (customerDisplayName: string) => (
  <FormattedMessage
    defaultMessage='Meet with a provider, covered in full or in part by {customerDisplayName}.'
    description='Generic cost messaging modal description for dynamic session limits'
    values={{ customerDisplayName }}
  />
)

const coachingForYourself = (numberOfSessions: number, customerDisplayName: string) => {
  return (
    <FormattedMessage
      defaultMessage='Work with a mental health coach {isUnlimitedCoaching, select, true {with unlimited sessions } other {up to {numberOfSessions} times a year—}}covered by {customerDisplayName}.'
      description='Cost messaging modal coaching session description text'
      values={{ numberOfSessions, customerDisplayName, isUnlimitedCoaching: isUnlimitedSessionLimit(numberOfSessions) }}
    />
  )
}

const coachingForFamily = (numberOfSessions: number, customerDisplayName: string) => {
  return (
    <FormattedMessage
      defaultMessage='Work with a mental health coach {isUnlimitedCoaching, select, true {with unlimited sessions } other {up to {numberOfSessions} times a year—}}covered by {customerDisplayName} for you and your dependents (18+). '
      description='Cost messaging modal coaching session description for family text'
      values={{ numberOfSessions, customerDisplayName, isUnlimitedCoaching: isUnlimitedSessionLimit(numberOfSessions) }}
    />
  )
}

export const coachingForParents = (numberOfSessions: string, customerName: string) => {
  return (
    <FormattedMessage
      defaultMessage='Work with a mental health coach for parenting up to {numberOfSessions} times per issue, per year—covered by {customerName}.'
      description='Cost messaging modal coaching for parents description text'
      values={{ numberOfSessions, customerName }}
    />
  )
}

export const therapyForChildren = (numberOfSessions: string, customerName: string) => {
  return (
    <FormattedMessage
      defaultMessage='Your children (ages 0-17) can meet with a provider up to {numberOfSessions} times per issue, per year—covered by {customerName}.'
      description='Cost messaging modal therapy for children description text'
      values={{ numberOfSessions, customerName }}
    />
  )
}

const therapyPerIssuePerYearForYourself = (numberOfSessions: number, customerDisplayName: string) => {
  return (
    <FormattedMessage
      defaultMessage='Meet with a provider up to {numberOfSessions} times per issue, per year—covered by {customerDisplayName}.'
      description='Cost messaging modal therapy session description for per issue per year text'
      values={{ numberOfSessions, customerDisplayName }}
    />
  )
}

const therapyPerIssuePerYearForFamily = (numberOfSessions: number, customerDisplayName: string, customerName = '') => {
  const hasSessionsCopy = CUSTOMERS_WITH_SESSIONS_COPY.includes(customerName)
  return (
    <FormattedMessage
      defaultMessage='Meet with a provider up to {numberOfSessions} {hasSessionsCopy, select, true {sessions} other {times}} per issue, per year—covered by {customerDisplayName} for you and your dependents.'
      description='Cost messaging modal therapy session description for per issue per year text with dependents'
      values={{ numberOfSessions, customerDisplayName, hasSessionsCopy: hasSessionsCopy ? 'true' : 'false' }}
    />
  )
}

const coachingPerIssuePerYearForYourself = (numberOfSessions: number, customerDisplayName: string) => {
  return (
    <FormattedMessage
      defaultMessage='Work with a mental health coach up to {numberOfSessions} times per issue, per year—covered by {customerDisplayName}.'
      description='Cost messaging modal coaching session description for per issue per year text'
      values={{ numberOfSessions, customerDisplayName }}
    />
  )
}

const coachingPerIssuePerYearForFamily = (numberOfSessions: number, customerDisplayName: string) => {
  return (
    <FormattedMessage
      defaultMessage='Work with a mental health coach up to {numberOfSessions} times per issue, per year—covered by {customerDisplayName} for you and your dependents (18+).'
      description='Cost messaging modal coaching session description for per issue per year text with dependents'
      values={{ numberOfSessions, customerDisplayName }}
    />
  )
}

export const eapMedsMedicationConsulationDescription = (
  <FormattedMessage
    defaultMessage='Meet with a prescriber—covered under your total sessions for the year.'
    description='Cost messaging modal description for customers with EAP medication enabled'
  />
)

export const medicationConsulationDescription = (
  <FormattedMessage
    defaultMessage='Your health plan may share the cost to meet with a prescriber.'
    description='Cost messaging modal description for medication consulation text'
  />
)

const hpiTherapyDescriptionForYourself = (
  <FormattedMessage
    defaultMessage='Cost sharing may be available to meet with a provider—depending on your health plan.'
    description='Cost messaging modal HPI cost sharing description for yourself text'
  />
)

const hpiTherapyDescriptionForFamily = (
  <FormattedMessage
    defaultMessage='Cost sharing may be available for you and your dependents to meet with a provider—depending on your health plan.'
    description='Cost messaging modal HPI cost sharing description for family text'
  />
)

export const medicalConsultationTitle = (
  <FormattedMessage
    defaultMessage='Medication consultations'
    description='Cost messaging modal medication consultation section title text'
  />
)

export const additionalHealthPlanSupportTitle = (
  <FormattedMessage
    defaultMessage='Additional health plan support'
    description='Cost messaging modal additional health plan section title text'
  />
)

export const neurodiversitySessionsTitle = (
  <FormattedMessage
    defaultMessage='Support for neurodiversity'
    description='Cost messaging modal neurodiversity sessions section title text'
  />
)

const additionalHealthPlanSupportDescription = (
  <FormattedMessage
    defaultMessage='Cost sharing may be available for more sessions—depending on your health plan.'
    description='Cost messaging modal for additional health plan sessions description text'
  />
)

const additionalHealthPlanSupportEAPCoachingHPITherapyDescription = (
  <FormattedMessage
    defaultMessage='Cost sharing may be available for more therapy sessions—depending on your health plan.'
    description='Cost messaging modal for additional health plan therapy sessions description text'
  />
)

export const therapyOrCoachingDescription = (
  <FormattedMessage
    defaultMessage='Cost sharing may be available for therapy and coaching sessions for you and your adult dependents, depending on your health plan.'
    description='Cost messaging modal for therapy or coaching sessions description text'
  />
)

const getDefaultTitle = (offersCoaching?: boolean, offersTherapy?: boolean) => {
  if (offersCoaching && !offersTherapy) return coachingSessionsTitle
  if (!offersCoaching && offersTherapy) return therapySessionsTitle
  return therapyOrCoachingTitle
}

export const eapCoachingAndHpiTherapyWithDependentsBaseContent = ({
  numberOfSessions,
  customerDisplayName,
  isCustomerDynamicSessionLimitEnabled,
  customerName,
}: EAP_COST_MESSAGING_PARAMS) => {
  return {
    title: coachingSessionsTitle,
    description: eapSessionForFamily(
      numberOfSessions,
      customerDisplayName,
      isCustomerDynamicSessionLimitEnabled,
      customerName,
    ),
    contentType: ProgramNames.Coaching,
  }
}

export const eapCoachingAndHpiTherapyNoDependentsBaseContent = ({
  numberOfSessions,
  customerDisplayName,
  isCustomerDynamicSessionLimitEnabled,
}: EAP_COST_MESSAGING_PARAMS) => {
  return {
    title: coachingSessionsTitle,
    description: eapSessionForYourself(numberOfSessions, customerDisplayName, !!isCustomerDynamicSessionLimitEnabled),
    contentType: ProgramNames.Coaching,
  }
}

export const eapOnlyNoDependentsBaseTexts = ({
  numberOfSessions,
  customerDisplayName,
  numberOfSessionsByProgram,
  displayVisitsPerIssuePerYearByProgram,
  isCustomerDynamicSessionLimitEnabled,
  customerOffersCoaching,
  customerOffersTherapy,
  customerOffersNeuroskillsCoaching,
  customerOffersCaregiverTraining,
}: EAP_COST_MESSAGING_PARAMS) => {
  const result: CostMessagingModalContent[] = []

  if (displayVisitsPerIssuePerYearByProgram) {
    const therapyPerIssuePerYearCount = displayVisitsPerIssuePerYearByProgram.therapy
    const coachingPerIssuePerYearCount = displayVisitsPerIssuePerYearByProgram.coaching
    const hasTherapyPerIssuePerYear = !!therapyPerIssuePerYearCount
    const hasCoachingPerIssuePerYear = !!coachingPerIssuePerYearCount

    if (hasTherapyPerIssuePerYear) {
      result.push({
        title: therapySessionsTitle,
        description: therapyPerIssuePerYearForYourself(therapyPerIssuePerYearCount, customerDisplayName),
        contentType: ProgramNames.BlendedCareTherapy,
      })
    }

    if (hasCoachingPerIssuePerYear) {
      result.push({
        title: coachingSessionsTitle,
        description: coachingPerIssuePerYearForYourself(coachingPerIssuePerYearCount, customerDisplayName),
        contentType: ProgramNames.Coaching,
      })
    }

    if (numberOfSessionsByProgram?.coaching && numberOfSessionsByProgram.therapy) {
      if (!hasTherapyPerIssuePerYear && numberOfSessionsByProgram.therapy) {
        result.push({
          title: therapySessionsTitle,
          description: eapSessionForYourself(
            numberOfSessionsByProgram.therapy,
            customerDisplayName,
            isCustomerDynamicSessionLimitEnabled,
          ),
          contentType: ProgramNames.BlendedCareTherapy,
        })
      }
      if (!hasCoachingPerIssuePerYear && numberOfSessionsByProgram.coaching) {
        result.push({
          title: coachingSessionsTitle,
          description: coachingForYourself(numberOfSessionsByProgram.coaching, customerDisplayName),
          contentType: ProgramNames.Coaching,
        })
      }
    }

    if (numberOfSessionsByProgram?.neurodiversity) {
      result.push({
        title: neurodiversitySessionsTitle,
        description: neurodiversitySessionsForYourself(
          numberOfSessionsByProgram.neurodiversity,
          customerOffersNeuroskillsCoaching,
          customerOffersCaregiverTraining,
        ),
        contentType: EAP_NEURO_CONTENT_TYPE,
      })
    }

    return result
  }

  if (numberOfSessionsByProgram?.coaching && numberOfSessionsByProgram?.therapy) {
    result.push(
      {
        title: therapySessionsTitle,
        description: eapSessionForYourself(
          numberOfSessionsByProgram.therapy ?? 0,
          customerDisplayName,
          isCustomerDynamicSessionLimitEnabled,
        ),
        contentType: ProgramNames.BlendedCareTherapy,
      },
      {
        title: coachingSessionsTitle,
        description: coachingForYourself(numberOfSessionsByProgram.coaching ?? 0, customerDisplayName),
        contentType: ProgramNames.Coaching,
      },
    )
  } else {
    result.push({
      title: getDefaultTitle(customerOffersCoaching, customerOffersTherapy),
      description: eapSessionForYourself(numberOfSessions, customerDisplayName, isCustomerDynamicSessionLimitEnabled),
      contentType: ProgramNames.BlendedCareTherapy,
    })
  }
  if (numberOfSessionsByProgram?.neurodiversity) {
    result.push({
      title: neurodiversitySessionsTitle,
      description: neurodiversitySessionsForYourself(
        numberOfSessionsByProgram.neurodiversity,
        customerOffersNeuroskillsCoaching,
        customerOffersCaregiverTraining,
      ),
      contentType: EAP_NEURO_CONTENT_TYPE,
    })
  }
  return result
}

export const eapOnlyWithDependentsBaseTexts = ({
  numberOfSessions,
  customerDisplayName,
  numberOfSessionsByProgram,
  displayVisitsPerIssuePerYearByProgram,
  isCustomerDynamicSessionLimitEnabled,
  customerName,
  customerOffersCoaching,
  customerOffersTherapy,
  customerOffersNeuroskillsCoaching,
  customerOffersCaregiverTraining,
}: EAP_COST_MESSAGING_PARAMS) => {
  const result: CostMessagingModalContent[] = []
  if (displayVisitsPerIssuePerYearByProgram) {
    const therapyPerIssuePerYearCount = displayVisitsPerIssuePerYearByProgram.therapy
    const coachingPerIssuePerYearCount = displayVisitsPerIssuePerYearByProgram.coaching
    const hasTherapyPerIssuePerYear = !!therapyPerIssuePerYearCount
    const hasCoachingPerIssuePerYear = !!coachingPerIssuePerYearCount

    if (hasTherapyPerIssuePerYear) {
      result.push({
        title: therapySessionsTitle,
        description: therapyPerIssuePerYearForFamily(therapyPerIssuePerYearCount, customerDisplayName, customerName),
        contentType: ProgramNames.BlendedCareTherapy,
      })
    }

    if (hasCoachingPerIssuePerYear) {
      result.push({
        title: coachingSessionsTitle,
        description: coachingPerIssuePerYearForFamily(coachingPerIssuePerYearCount, customerDisplayName),
        contentType: ProgramNames.Coaching,
      })
    }

    if (numberOfSessionsByProgram?.coaching && numberOfSessionsByProgram?.therapy) {
      if (!hasTherapyPerIssuePerYear && numberOfSessionsByProgram.therapy) {
        result.push({
          title: therapySessionsTitle,
          description: eapSessionForFamily(
            numberOfSessionsByProgram.therapy,
            customerDisplayName,
            isCustomerDynamicSessionLimitEnabled,
            customerName,
          ),
          contentType: ProgramNames.BlendedCareTherapy,
        })
      }
      if (!hasCoachingPerIssuePerYear && numberOfSessionsByProgram.coaching) {
        result.push({
          title: coachingSessionsTitle,
          description: coachingForFamily(numberOfSessionsByProgram.coaching, customerDisplayName),
          contentType: ProgramNames.Coaching,
        })
      }
    }

    if (numberOfSessionsByProgram?.neurodiversity) {
      result.push({
        title: neurodiversitySessionsTitle,
        description: neurodiversitySessionsForFamily(
          numberOfSessionsByProgram.neurodiversity,
          customerOffersNeuroskillsCoaching,
          customerOffersCaregiverTraining,
        ),
        contentType: EAP_NEURO_CONTENT_TYPE,
      })
    }

    return result
  }

  if (numberOfSessionsByProgram?.coaching && numberOfSessionsByProgram?.therapy) {
    result.push(
      {
        title: therapySessionsTitle,
        description: eapSessionForFamily(
          numberOfSessionsByProgram.therapy ?? 0,
          customerDisplayName,
          isCustomerDynamicSessionLimitEnabled,
          customerName,
        ),
        contentType: ProgramNames.BlendedCareTherapy,
      },
      {
        title: coachingSessionsTitle,
        description: coachingForFamily(numberOfSessionsByProgram.coaching ?? 0, customerDisplayName),
        contentType: ProgramNames.Coaching,
      },
    )
  } else {
    result.push({
      title: getDefaultTitle(customerOffersCoaching, customerOffersTherapy),
      description: eapSessionForFamily(
        numberOfSessions,
        customerDisplayName,
        isCustomerDynamicSessionLimitEnabled,
        customerName,
      ),
      contentType: ProgramNames.BlendedCareTherapy,
    })
  }
  if (numberOfSessionsByProgram?.neurodiversity) {
    result.push({
      title: neurodiversitySessionsTitle,
      description: neurodiversitySessionsForFamily(
        numberOfSessionsByProgram.neurodiversity,
        customerOffersNeuroskillsCoaching,
        customerOffersCaregiverTraining,
      ),
      contentType: EAP_NEURO_CONTENT_TYPE,
    })
  }
  return result
}

export const additionalHealthPlanSupportText = {
  title: additionalHealthPlanSupportTitle,
  description: additionalHealthPlanSupportDescription,
  contentType: ProgramNames.ClinicalLeaveEvaluation,
}

export const additionalHealthPlanSupportEAPCoachingHPITherapyText = {
  title: additionalHealthPlanSupportTitle,
  description: additionalHealthPlanSupportEAPCoachingHPITherapyDescription,
  contentType: ProgramNames.ClinicalLeaveEvaluation,
}

export const hpiOnlyNoDependentsBaseTexts = [
  {
    title: therapySessionsTitle,
    description: hpiTherapyDescriptionForYourself,
    contentType: ProgramNames.BlendedCareTherapy,
  },
]

export const hpiWithDependentsBaseTexts = [
  {
    title: therapySessionsTitle,
    description: hpiTherapyDescriptionForFamily,
    contentType: ProgramNames.BlendedCareTherapy,
  },
]

export const hpiMedsBaseDescription = {
  title: medicalConsultationTitle,
  description: medicationConsulationDescription,
  contentType: ProgramNames.MedicationManagement,
}

export const eapMedsBaseDescription = {
  title: medicalConsultationTitle,
  description: eapMedsMedicationConsulationDescription,
  contentType: EAP_MEDS_CONTENT_TYPE,
}

const getMedsTextIfEnabled = ({
  isCustomerEAPMeds,
  isCustomerHPIMeds,
  coverageType,
  isCustomerProgramEAPCoaching,
  isCustomerProgramHPITherapy,
  isCustomerProgramEAPTherapy,
}: GetCostMessagingModalTextParams & {
  coverageType: CoverageType
}): CostMessagingModalContent[] => {
  const medsTexts = []

  // Order of the if statements are important because we want to opt for the EAP copy if the program is both EAP and HPI supported
  if (isCustomerEAPMeds) {
    medsTexts.push(eapMedsBaseDescription)
  } else if (isCustomerHPIMeds) {
    medsTexts.push(hpiMedsBaseDescription)
  }

  if (coverageType === CoverageType.EAP_AND_HPI) {
    if (isCustomerProgramEAPCoaching && isCustomerProgramHPITherapy && !isCustomerProgramEAPTherapy) {
      medsTexts.push(additionalHealthPlanSupportEAPCoachingHPITherapyText)
    } else {
      medsTexts.push(additionalHealthPlanSupportText)
    }
  }

  return medsTexts
}

const getEapOnlyCostMessagingModalText = ({
  isDependentDisabled,
  isCustomerEAPMeds,
  numberOfSessions,
  customerDisplayName,
  isCustomerProgramEAPCoaching,
  isCustomerProgramHPITherapy,
  isCustomerProgramEAPTherapy,
  isCustomerNeuroskillsCoachingEnabled,
  isCustomerCaregiverTrainingEnabled,
  numberOfSessionsByProgram,
  displayVisitsPerIssuePerYearByProgram,
  isCustomerDynamicSessionLimitEnabled,
  customerName,
}: GetCostMessagingModalTextParams & EAP_COST_MESSAGING_PARAMS) => {
  const defaultMedsTextParams = {
    coverageType: CoverageType.EAP_ONLY,
    isCustomerEAPMeds,
    dependentsEnabled: !isDependentDisabled,
  }
  const customerOffersCoaching = isCustomerProgramEAPCoaching
  const customerOffersTherapy = isCustomerProgramEAPTherapy || isCustomerProgramHPITherapy

  let eapOnlyModalContent: CostMessagingModalContent[] = []
  if (!isDependentDisabled) {
    eapOnlyModalContent = [
      ...eapOnlyWithDependentsBaseTexts({
        numberOfSessions,
        customerDisplayName,
        numberOfSessionsByProgram,
        displayVisitsPerIssuePerYearByProgram,
        isCustomerDynamicSessionLimitEnabled,
        customerName,
        customerOffersCoaching,
        customerOffersTherapy,
        customerOffersNeuroskillsCoaching: isCustomerNeuroskillsCoachingEnabled,
        customerOffersCaregiverTraining: isCustomerCaregiverTrainingEnabled,
      }),
    ]
  } else {
    eapOnlyModalContent = [
      ...eapOnlyNoDependentsBaseTexts({
        numberOfSessions,
        customerDisplayName,
        numberOfSessionsByProgram,
        displayVisitsPerIssuePerYearByProgram,
        isCustomerDynamicSessionLimitEnabled,
        customerOffersCoaching,
        customerOffersTherapy,
        customerOffersNeuroskillsCoaching: isCustomerNeuroskillsCoachingEnabled,
        customerOffersCaregiverTraining: isCustomerCaregiverTrainingEnabled,
      }),
    ]
  }

  const medsText = getMedsTextIfEnabled(defaultMedsTextParams)
  if (medsText) {
    eapOnlyModalContent = [...eapOnlyModalContent, ...medsText]
  }

  return eapOnlyModalContent
}

const getHpiOnlyCostMessagingModalText = ({
  isDependentDisabled,
  isCustomerHPIMeds,
}: GetCostMessagingModalTextParams) => {
  const defaultMedsTextParams = {
    coverageType: CoverageType.HPI_ONLY,
    isCustomerHPIMeds,
  }

  let hpiOnlyModalContent: CostMessagingModalContent[] = []
  if (!isDependentDisabled) {
    hpiOnlyModalContent = [...hpiWithDependentsBaseTexts]
  } else {
    hpiOnlyModalContent = [...hpiOnlyNoDependentsBaseTexts]
  }

  const medsText = getMedsTextIfEnabled(defaultMedsTextParams)
  if (medsText) {
    hpiOnlyModalContent = [...hpiOnlyModalContent, ...medsText]
  }

  return hpiOnlyModalContent
}

const getEapAndHpiCostMessagingModalText = ({
  isDependentDisabled,
  isCustomerEAPMeds,
  isCustomerHPIMeds,
  isCustomerProgramEAPCoaching,
  isCustomerProgramHPITherapy,
  isCustomerProgramEAPTherapy,
  isCustomerNeuroskillsCoachingEnabled,
  isCustomerCaregiverTrainingEnabled,
  numberOfSessions,
  customerDisplayName,
  numberOfSessionsByProgram,
  displayVisitsPerIssuePerYearByProgram,
  isCustomerDynamicSessionLimitEnabled,
  customerName,
}: GetCostMessagingModalTextParams & EAP_COST_MESSAGING_PARAMS) => {
  let eapAndHpiCostMessagingModalText: CostMessagingModalContent[] = []

  const eapCoachingAndHpiTherapyBaseContent = !isDependentDisabled
    ? eapCoachingAndHpiTherapyWithDependentsBaseContent
    : eapCoachingAndHpiTherapyNoDependentsBaseContent

  const eapCoachingAndEapTherapyBaseContent = !isDependentDisabled
    ? eapOnlyWithDependentsBaseTexts
    : eapOnlyNoDependentsBaseTexts

  const defaultMedsTextParams = {
    coverageType: CoverageType.EAP_AND_HPI,
    isCustomerEAPMeds,
    isCustomerHPIMeds,
    isCustomerProgramEAPCoaching,
    isCustomerProgramHPITherapy,
    isCustomerProgramEAPTherapy,
    dependentsEnabled: !isDependentDisabled,
  }

  const therapyIsOnlyHpiSupported = isCustomerProgramHPITherapy && !isCustomerProgramEAPTherapy

  if (isCustomerProgramEAPCoaching && numberOfSessions && customerDisplayName) {
    // a program can be both EAP and HPI supported we want to enter the if block only when therapy is strictly HPI only
    if (therapyIsOnlyHpiSupported) {
      eapAndHpiCostMessagingModalText = [
        eapCoachingAndHpiTherapyBaseContent({
          numberOfSessions,
          customerDisplayName,
          isCustomerDynamicSessionLimitEnabled: !!isCustomerDynamicSessionLimitEnabled,
          customerName,
        }),
      ]
    } else {
      eapAndHpiCostMessagingModalText = eapCoachingAndEapTherapyBaseContent({
        numberOfSessions,
        customerDisplayName,
        numberOfSessionsByProgram,
        displayVisitsPerIssuePerYearByProgram,
        isCustomerDynamicSessionLimitEnabled: !!isCustomerDynamicSessionLimitEnabled,
        customerName,
        customerOffersNeuroskillsCoaching: isCustomerNeuroskillsCoachingEnabled,
        customerOffersCaregiverTraining: isCustomerCaregiverTrainingEnabled,
      })
    }

    const medsText = getMedsTextIfEnabled(defaultMedsTextParams)
    // there should at least be one other text in order to add the meds text, i.e. modal content can not only contain meds text
    if (eapAndHpiCostMessagingModalText.length > 0 && medsText) {
      eapAndHpiCostMessagingModalText = [...eapAndHpiCostMessagingModalText, ...medsText]
    }
  }

  return eapAndHpiCostMessagingModalText
}

export const getDefaultCostMessagingModalText = (
  numberOfSessions: string,
  customerDisplayName: string,
  numberOfSessionsByProgramStr?: string,
  displayVisitsPerIssuePerYearByProgramStr?: string,
  isCustomerDynamicSessionLimitEnabled?: boolean,
  customerName?: string,
): {
  [key in CoverageType]: (params: GetCostMessagingModalTextParams) => CostMessagingModalContent[]
} => {
  const numberOfSessionsByProgram = numberOfSessionsByProgramStr
    ? (JSON.parse(numberOfSessionsByProgramStr) as SessionLimitByProgram)
    : undefined

  const displayVisitsPerIssuePerYearByProgram = displayVisitsPerIssuePerYearByProgramStr
    ? (JSON.parse(displayVisitsPerIssuePerYearByProgramStr) as SessionCountByProgram)
    : undefined

  const sessionCountToDisplay = numberOfSessionsByProgram
    ? computeRemainderSessions(Number(numberOfSessions), numberOfSessionsByProgram).toString()
    : numberOfSessions

  return {
    [CoverageType.EAP_ONLY]: (params: GetCostMessagingModalTextParams) => {
      return getEapOnlyCostMessagingModalText({
        numberOfSessions: sessionCountToDisplay.toString(),
        customerDisplayName,
        numberOfSessionsByProgram,
        displayVisitsPerIssuePerYearByProgram,
        isCustomerDynamicSessionLimitEnabled: !!isCustomerDynamicSessionLimitEnabled,
        customerName,
        ...params,
      })
    },
    [CoverageType.HPI_ONLY]: (params: GetCostMessagingModalTextParams) => {
      return getHpiOnlyCostMessagingModalText({ ...params })
    },
    [CoverageType.EAP_AND_HPI]: (params: GetCostMessagingModalTextParams) => {
      return getEapAndHpiCostMessagingModalText({
        numberOfSessions: sessionCountToDisplay.toString(),
        numberOfSessionsByProgram,
        customerDisplayName,
        displayVisitsPerIssuePerYearByProgram,
        isCustomerDynamicSessionLimitEnabled: !!isCustomerDynamicSessionLimitEnabled,
        customerName,
        ...params,
      })
    },
  }
}

export const getSessionsCoverageText = (
  params: GetCostMessagingModalTextParams & {
    coverageType: CoverageType
    numberOfSessions?: string
    numberOfSessionsByProgram?: SessionLimitByProgram
    displayVisitsPerIssuePerYear?: number
    displayVisitsPerIssuePerYearByProgram?: SessionCountByProgram
  },
): SessionsCoverageText | undefined => {
  const {
    coverageType,
    isDependentDisabled,
    numberOfSessions,
    numberOfSessionsByProgram,
    displayVisitsPerIssuePerYear,
    displayVisitsPerIssuePerYearByProgram,
    isCustomerProgramEAPCoaching,
    isCustomerProgramEAPTherapy,
    isCustomerProgramHPITherapy,
  } = params

  const hasDependents = !isDependentDisabled
  const hasPerIssuePerYear = !!displayVisitsPerIssuePerYearByProgram || !!displayVisitsPerIssuePerYear
  const hasDualSessions = !!numberOfSessionsByProgram?.coaching && !!numberOfSessionsByProgram?.therapy
  const isEapTherapyOnly = isCustomerProgramEAPTherapy && !isCustomerProgramEAPCoaching
  const therapyIsOnlyHpiSupported = isCustomerProgramHPITherapy && !isCustomerProgramEAPTherapy
  const hasTherapyProgram = isCustomerProgramHPITherapy || isCustomerProgramEAPTherapy

  const { displaySessions: displayNumberOfSessions, messageId: sessionsTitleMessageId } = getSessionCountForDisplay({
    numSessions: numberOfSessions,
    numberOfSessionsByProgram,
    displayVisitsPerIssuePerYear,
    displayVisitsPerIssuePerYearByProgram,
  })

  // HPI Only
  if (coverageType === CoverageType.HPI_ONLY) {
    return {
      title: therapySessionsTitle,
      description: (
        <FormattedMessage
          {...(hasDependents
            ? sessionsCoverageMessages.hpiWithDependentsDescription
            : sessionsCoverageMessages.hpiNoDependentsDescription)}
        />
      ),
    }
  }

  // EAP Only + coaching (no therapy)
  if (coverageType === CoverageType.EAP_ONLY && isCustomerProgramEAPCoaching && !hasTherapyProgram) {
    return {
      title: (
        <FormattedMessage
          {...sessionsCoverageMessages[sessionsTitleMessageId]}
          values={{ numberOfSessions: displayNumberOfSessions }}
        />
      ),
      description: (
        <FormattedMessage
          {...(hasDependents
            ? sessionsCoverageMessages.eapOnlyHasCoachingOnlyWithDependentsDescription
            : sessionsCoverageMessages.eapOnlyHasCoachingOnlyWithNoDependentsDescription)}
        />
      ),
    }
  }

  // EAP + HPI: EAP Coaching + HPI Therapy
  if (coverageType === CoverageType.EAP_AND_HPI && therapyIsOnlyHpiSupported) {
    return {
      title: (
        <FormattedMessage
          {...sessionsCoverageMessages[sessionsTitleMessageId]}
          values={{ numberOfSessions: displayNumberOfSessions }}
        />
      ),
      description: (
        <FormattedMessage
          {...(hasDependents
            ? sessionsCoverageMessages.eapCoachingHpiTherapyWithDependentsDescription
            : sessionsCoverageMessages.eapCoachingHpiTherapyNoDependentsDescription)}
        />
      ),
    }
  }

  // EAP Only OR EAP + HPI (when therapy is EAP supported)
  if (coverageType === CoverageType.EAP_ONLY || coverageType === CoverageType.EAP_AND_HPI) {
    if (hasPerIssuePerYear) {
      return {
        title: (
          <FormattedMessage
            {...sessionsCoverageMessages[sessionsTitleMessageId]}
            values={{ numberOfSessions: displayNumberOfSessions }}
          />
        ),
        description: (
          <FormattedMessage
            {...(hasDependents
              ? sessionsCoverageMessages.eapPerIssueWithDependentsDescription
              : sessionsCoverageMessages.eapPerIssueNoDependentsDescription)}
          />
        ),
      }
    }

    if (hasDualSessions) {
      return {
        title: (
          <FormattedMessage
            {...sessionsCoverageMessages[sessionsTitleMessageId]}
            values={{ numberOfSessions: displayNumberOfSessions }}
          />
        ),
        description: (
          <FormattedMessage
            {...(hasDependents
              ? sessionsCoverageMessages.eapWithDependentsDescription
              : sessionsCoverageMessages.eapNoDependentsDescription)}
          />
        ),
      }
    }

    if (isEapTherapyOnly) {
      return {
        title: (
          <FormattedMessage
            {...sessionsCoverageMessages[sessionsTitleMessageId]}
            values={{ numberOfSessions: displayNumberOfSessions }}
          />
        ),
        description: (
          <FormattedMessage
            {...(hasDependents
              ? sessionsCoverageMessages.eapTherapyOnlyWithDependentsDescription
              : sessionsCoverageMessages.eapTherapyOnlyNoDependentsDescription)}
          />
        ),
      }
    }

    return {
      title: (
        <FormattedMessage
          {...sessionsCoverageMessages[sessionsTitleMessageId]}
          values={{ numberOfSessions: displayNumberOfSessions }}
        />
      ),
      description: (
        <FormattedMessage
          {...(hasDependents
            ? sessionsCoverageMessages.eapWithDependentsDescription
            : sessionsCoverageMessages.eapNoDependentsDescription)}
        />
      ),
    }
  }

  return undefined
}
